import React from 'react';

/**
 * LoadingSpinner - Spinner di caricamento
 */
export function LoadingSpinner({
  size = 'md',
  color = 'primary',
  className = ''
}) {
  const sizeClasses = {
    xs: 'w-4 h-4',
    sm: 'w-5 h-5',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  const colorClasses = {
    primary: 'text-primary-600',
    white: 'text-white',
    gray: 'text-gray-400'
  };

  return (
    <svg
      className={`animate-spin ${sizeClasses[size]} ${colorClasses[color]} ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

/**
 * LoadingPage - Pagina di caricamento fullscreen
 */
export function LoadingPage({ message = 'Caricamento...' }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
      <LoadingSpinner size="xl" />
      <p className="mt-4 text-gray-600 text-lg">{message}</p>
    </div>
  );
}

/**
 * LoadingOverlay - Overlay di caricamento
 */
export function LoadingOverlay({
  isLoading,
  children,
  message = 'Caricamento...',
  blur = true
}) {
  return (
    <div className="relative">
      {children}
      {isLoading && (
        <div
          className={`absolute inset-0 bg-white/80 flex flex-col items-center justify-center z-50 ${
            blur ? 'backdrop-blur-sm' : ''
          }`}
        >
          <LoadingSpinner size="lg" />
          {message && (
            <p className="mt-3 text-gray-600 text-sm">{message}</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * LoadingCard - Skeleton card per caricamento
 */
export function LoadingCard({ lines = 3 }) {
  return (
    <div className="bg-white rounded-lg shadow p-6 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="h-3 bg-gray-200 rounded mb-2"
          style={{ width: `${Math.random() * 40 + 60}%` }}
        />
      ))}
    </div>
  );
}

/**
 * LoadingTable - Skeleton table per caricamento
 */
export function LoadingTable({ rows = 5, columns = 4 }) {
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden animate-pulse">
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b flex gap-4">
        {Array.from({ length: columns }).map((_, i) => (
          <div
            key={i}
            className="h-4 bg-gray-200 rounded"
            style={{ width: `${100 / columns}%` }}
          />
        ))}
      </div>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="px-4 py-3 border-b flex gap-4">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <div
              key={colIndex}
              className="h-4 bg-gray-200 rounded"
              style={{
                width: `${100 / columns}%`,
                opacity: 1 - rowIndex * 0.1
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * LoadingList - Skeleton list per caricamento
 */
export function LoadingList({ items = 3 }) {
  return (
    <div className="space-y-4 animate-pulse">
      {Array.from({ length: items }).map((_, i) => (
        <div
          key={i}
          className="bg-white rounded-lg shadow p-4 flex items-center gap-4"
        >
          <div className="w-12 h-12 bg-gray-200 rounded-full" />
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * LoadingStats - Skeleton per stat cards
 */
export function LoadingStats({ count = 4 }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow p-6">
          <div className="h-3 bg-gray-200 rounded w-1/2 mb-3" />
          <div className="h-8 bg-gray-200 rounded w-1/3" />
        </div>
      ))}
    </div>
  );
}

/**
 * LoadingForm - Skeleton per form
 */
export function LoadingForm({ fields = 4 }) {
  return (
    <div className="bg-white rounded-lg shadow p-6 animate-pulse space-y-6">
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i}>
          <div className="h-3 bg-gray-200 rounded w-1/4 mb-2" />
          <div className="h-10 bg-gray-200 rounded w-full" />
        </div>
      ))}
      <div className="flex gap-3 pt-4">
        <div className="h-10 bg-gray-200 rounded w-24" />
        <div className="h-10 bg-gray-200 rounded w-24" />
      </div>
    </div>
  );
}

/**
 * EmptyState - Stato vuoto con messaggio
 */
export function EmptyState({
  icon,
  title = 'Nessun dato disponibile',
  message,
  action,
  actionLabel,
  className = ''
}) {
  return (
    <div className={`text-center py-12 px-4 ${className}`}>
      {icon ? (
        <div className="mx-auto w-16 h-16 text-gray-300 mb-4">{icon}</div>
      ) : (
        <svg
          className="mx-auto w-16 h-16 text-gray-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>
      )}
      <h3 className="mt-2 text-lg font-medium text-gray-900">{title}</h3>
      {message && (
        <p className="mt-1 text-sm text-gray-500">{message}</p>
      )}
      {action && (
        <div className="mt-6">
          <button
            onClick={action}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            {actionLabel || 'Crea nuovo'}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * ErrorState - Stato di errore
 */
export function ErrorState({
  title = 'Si è verificato un errore',
  message = 'Impossibile caricare i dati. Riprova più tardi.',
  onRetry,
  className = ''
}) {
  return (
    <div className={`text-center py-12 px-4 ${className}`}>
      <svg
        className="mx-auto w-16 h-16 text-red-300"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <h3 className="mt-2 text-lg font-medium text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-500">{message}</p>
      {onRetry && (
        <div className="mt-6">
          <button
            onClick={onRetry}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Riprova
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * useLoadingState - Hook per gestire stati di caricamento
 */
export function useLoadingState(initialLoading = false) {
  const [state, setState] = React.useState({
    isLoading: initialLoading,
    error: null,
    data: null
  });

  const setLoading = React.useCallback(() => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
  }, []);

  const setSuccess = React.useCallback((data) => {
    setState({ isLoading: false, error: null, data });
  }, []);

  const setError = React.useCallback((error) => {
    setState(prev => ({ ...prev, isLoading: false, error }));
  }, []);

  const reset = React.useCallback(() => {
    setState({ isLoading: false, error: null, data: null });
  }, []);

  return {
    ...state,
    setLoading,
    setSuccess,
    setError,
    reset
  };
}

export default LoadingSpinner;
